import type { TFunction } from 'i18next';
import isEqual from 'lodash-es/isEqual';
import * as yup from 'yup';

import type { ContactIdentity } from '@/shared/types/contacts';
import { LENGTH_XS } from '@/util/validation/constants';
import { emailPattern } from '@/util/validation/patterns';

export const MAX_EMAILS = 10;
export const MAX_PHONES = 10;

export interface EmailRow {
  value: string;
  identityId?: string;
}

export interface PhoneRow {
  value: string;
  identityId?: string;
}

export interface ContactFormValues {
  name: string;
  emails: EmailRow[];
  phones: PhoneRow[];
  consent: boolean;
}

/**
 * Loose phone sanity check: rejects clearly non-numeric input (letters, or fewer
 * than three digits) without enforcing a strict format, so unusual-but-real
 * numbers still save. Strict E.164 validation is intentionally not applied here.
 */
const isPhoneLike = (value: string): boolean =>
  !/[a-zA-Z]/.test(value) && value.replace(/\D/g, '').length >= 3;

/**
 * Yup schema for the contact form. The "at least one email or phone" rule is
 * enforced imperatively in the panel so its message can render outside a
 * field-array row.
 *
 * `requireName` mirrors what the orchestrator will accept. `createSchema` has
 * `name.required()`, so a name is mandatory on create. `updateSchema` leaves the
 * key optional but the underlying rule is `min(1)`, so a name can be omitted but
 * never sent empty — i.e. a contact with no name (the normal shape for
 * inbound-created ones) is editable, while an existing name cannot be cleared.
 */
export const buildContactFormSchema = (t: TFunction, requireName = true) =>
  yup.object({
    name: (requireName
      ? yup.string().trim().required(t('contacts.errors.name_required'))
      : yup.string().trim()
    ).max(LENGTH_XS, t('contacts.errors.name_max', { 0: LENGTH_XS })),
    emails: yup
      .array()
      .of(
        yup.object({
          value: yup
            .string()
            .trim()
            .test('email-format', t('contacts.errors.email'), (v) => !v || emailPattern.test(v)),
          identityId: yup.string(),
        })
      )
      .default([]),
    phones: yup
      .array()
      .of(
        yup.object({
          value: yup
            .string()
            .trim()
            .test('phone-format', t('contacts.errors.phone'), (v) => !v || isPhoneLike(v)),
          identityId: yup.string(),
        })
      )
      .default([]),
    consent: yup.boolean().default(true),
  });

/** Trim + dedupe a list of raw string values, dropping blanks. */
const dedupeValues = (values: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const value = raw.trim();
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    out.push(value);
  }
  return out;
};

/** Non-empty, trimmed, deduped email values. */
export const cleanEmails = (rows: EmailRow[]): string[] => dedupeValues(rows.map((r) => r.value));

/** Non-empty, trimmed, deduped phone values. */
export const cleanPhones = (rows: PhoneRow[]): string[] => dedupeValues(rows.map((r) => r.value));

/** A comparable snapshot of meaningful form state, used for the dirty guard. */
export const toSnapshot = (values: ContactFormValues) => ({
  name: values.name.trim(),
  emails: cleanEmails(values.emails),
  phones: cleanPhones(values.phones),
  consent: values.consent,
});

export const isSnapshotEqual = (a: ContactFormValues, b: ContactFormValues): boolean =>
  isEqual(toSnapshot(a), toSnapshot(b));

/** The denormalized columns on `contacts` that mirror the first email / phone. */
export type ContactScalarField = 'email' | 'phone';

/**
 * Loose comparison key, used *only* to decide whether `contacts.email/phone`
 * still points at a value the form kept. Deliberately looser than the
 * orchestrator's normalization (E.164 / lowercase): the point is to skip a
 * pointless repoint when the same address is merely formatted differently. It is
 * never used to build a value we send.
 */
const looseKey = (field: ContactScalarField, value: string): string =>
  field === 'phone' ? value.replace(/\D/g, '') : value.trim().toLowerCase();

/**
 * The value `contacts.<field>` should be repointed to, or `undefined` to leave
 * the column alone.
 *
 * The contacts list renders `contact.email || contact.phone` — real stored
 * columns, not something derived from `contact_identities` — so an identity-only
 * edit leaves the list showing a number the user just deleted. Writing the column
 * fixes that, but `PUT /contacts/:id` also reconciles identities from it: it
 * soft-deletes the identity matching the OLD value and indexes the new one. So
 * the column may only be repointed when the old value is *not* one the form kept.
 *
 * - nothing left in the form → leave it (`updateSchema` has no `allow(null)`, so
 *   the column cannot be cleared from here)
 * - never set → point it at the first value
 * - still among the kept values (first or not) → leave it; repointing would
 *   retire a live identity the user wants, and the column is not stale anyway
 * - otherwise (the row was removed, or the column never matched an identity) →
 *   repoint to the first value
 */
export const pickScalarRepoint = (
  field: ContactScalarField,
  current: string | null | undefined,
  keptValues: string[]
): string | undefined => {
  const desired = keptValues[0];
  if (!desired) {
    return undefined;
  }
  if (!current) {
    return desired;
  }
  const currentKey = looseKey(field, current);
  return keptValues.some((value) => looseKey(field, value) === currentKey) ? undefined : desired;
};

/**
 * Swap a raw row value for the canonical one the server echoed back after a
 * contact write, keeping the row's `identityId` so the diff still retires the row
 * it replaces. Without this the identity upsert would write the raw text next to
 * the normalized row the contact write just indexed — two rows for one address.
 */
export const withCanonicalRowValue = <T extends { value: string }>(
  rows: T[],
  raw: string,
  canonical: string
): T[] => rows.map((row) => (row.value.trim() === raw ? { ...row, value: canonical } : row));

/** `withCanonicalRowValue` for a cleaned value list (create mode). */
export const withCanonicalFirstValue = (values: string[], canonical?: string): string[] => {
  if (!canonical || values.length === 0 || values[0] === canonical) {
    return values;
  }
  return [canonical, ...values.slice(1).filter((value) => value !== canonical)];
};

export interface IdentityUpsertOp {
  type: 'email' | 'phone';
  value: string;
  /** When set, delete this old identity once the upsert (a value change) succeeds. */
  replacesIdentityId?: string;
}

export interface IdentityDiff {
  upserts: IdentityUpsertOp[];
  /** Identities removed outright (row deleted by the user). */
  deletes: string[];
}

/**
 * Pair each cleaned value back with the identity id of the row it came from.
 *
 * When two rows collapse to the same value (the user retyped one identity to
 * match another that already exists), the identity that actually owns the value
 * wins. Picking the first matching row instead would leave the owner unmatched,
 * queue it for deletion, and wipe out the value the upsert just landed on.
 */
const withIdentityIds = (
  values: string[],
  rows: Array<{ value: string; identityId?: string }>,
  originals: ContactIdentity[]
): Array<{ value: string; identityId?: string }> =>
  values.map((value) => {
    const owner = originals.find((original) => original.value === value);
    return {
      value,
      identityId: owner?.identity_id ?? rows.find((r) => r.value.trim() === value)?.identityId,
    };
  });

/**
 * Diff current email/phone rows against the contact's existing identities.
 * - removed row            → delete
 * - value changed          → upsert new (then delete old on success)
 * - new row                → upsert
 */
export const diffIdentities = (
  values: ContactFormValues,
  identities: ContactIdentity[]
): IdentityDiff => {
  const upserts: IdentityUpsertOp[] = [];
  const deletes: string[] = [];

  const buildForType = (
    type: 'email' | 'phone',
    cleanedValues: string[],
    rows: Array<{ value: string; identityId?: string }>
  ) => {
    const originals = identities.filter((i) => i.type === type && !i.deleted_at);
    const currentRows = withIdentityIds(cleanedValues, rows, originals);
    const currentById = new Map(
      currentRows.filter((r) => r.identityId).map((r) => [r.identityId!, r])
    );

    for (const original of originals) {
      const current = currentById.get(original.identity_id);
      if (!current) {
        deletes.push(original.identity_id);
        continue;
      }
      if (current.value !== original.value) {
        upserts.push({ type, value: current.value, replacesIdentityId: original.identity_id });
      }
    }

    for (const row of currentRows) {
      if (!row.identityId) {
        upserts.push({ type, value: row.value });
      }
    }
  };

  buildForType('email', cleanEmails(values.emails), values.emails);
  buildForType('phone', cleanPhones(values.phones), values.phones);

  return { upserts, deletes };
};
